import React from 'react';
import { Flex, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';
import { useIsPrinting } from '~library/print';

export type DiscoverabilityKPICardProps = React.PropsWithChildren<{
  title: string;
  tooltip: string;
}>;

// Custom card chrome (no antd `Card`): the Figma layout uses a 56px header
// strip with horizontal-only padding and a value area with `paddingXS` on top
// and `paddingLG` on the other three sides — antd `Card` enforces uniform
// body padding so we build the shell from primitives instead.
function DiscoverabilityKPICard({ title, tooltip, children }: DiscoverabilityKPICardProps) {
  const { token } = theme.useToken();
  const { isPrinting } = useIsPrinting();

  return (
    <Flex
      vertical
      data-testid="discoverability-kpi-card"
      style={{
        flex: 1,
        minWidth: 310,
        backgroundColor: token.colorBgContainer,
        border: `${token.lineWidth}px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
      }}
    >
      <Flex
        align="center"
        gap={token.marginXXS}
        // FIXME: no exact token — 56px is the Figma card header strip height
        style={{ height: 56, paddingInline: token.paddingLG, flexShrink: 0 }}
      >
        <Typography.Text strong>{title}</Typography.Text>
        {/* Nothing to hover on paper, and the export spells each of these
            tooltips out in full beneath the cards, so the icon would only be
            a mark the reader cannot act on. */}
        {!isPrinting && (
          <CustomTooltip placement="top" message={tooltip}>
            <InfoCircleOutlined style={{ color: token.colorIcon }} />
          </CustomTooltip>
        )}
      </Flex>
      <Flex
        vertical
        style={{
          paddingInline: token.paddingLG,
          paddingTop: token.paddingXS,
          paddingBottom: token.paddingLG,
        }}
      >
        {children}
      </Flex>
    </Flex>
  );
}

export default DiscoverabilityKPICard;
